import * as React from 'react';
import { Button } from '@material-ui/core';
import { CheckboxGroupInput, Edit, FormDataConsumer, FunctionField, SelectInput, Show, SimpleForm, SimpleShowLayout, TextInput, useNotify, usePermissions, useRefresh } from 'react-admin';
import { httpClient } from '../utils/dataProvider';
import { API_BASE_URL } from '../utils/config';
import { ActionButton, Avatar, DetailGrid, DetailItem, Empty, PageHeader, ProfileBand, Search, Select, StatusPill, Surface, Toolbar } from '../components/ui';
import { ADMIN_ROLES, hasAccess } from '../utils/permissions';
import SubscriptionGrantPanel from '../components/SubscriptionGrantPanel';

export const UserList = props => {
    const [users, setUsers] = React.useState([]);
    const [countries, setCountries] = React.useState([]);
    const [cities, setCities] = React.useState([]);
    const [selectedCountry, setSelectedCountry] = React.useState('all');
    const [selectedCity, setSelectedCity] = React.useState('all');
    const [loading, setLoading] = React.useState(false);
    const [filter, setFilter] = React.useState('ALL');
    const [search, setSearch] = React.useState('');
    React.useEffect(() => {
        setLoading(true);
        httpClient(`${API_BASE_URL}/users/admin-list`).then(({ json }) => {
            const docs = json.docs || [];
            setUsers(docs);
            setCountries([...new Set(docs.map(user => user.country).filter(Boolean))]);
            setCities([...new Set(docs.map(user => user.city).filter(Boolean))]);
        }).catch(() => {
            // Fallback to original endpoint if admin-list is not available
            httpClient(`${API_BASE_URL}/users`).then(({ json }) => {
                const docs = json.docs || [];
                setUsers(docs);
                setCountries([...new Set(docs.map(user => user.country).filter(Boolean))]);
                setCities([...new Set(docs.map(user => user.city).filter(Boolean))]);
            });
        }).finally(() => setLoading(false));
    }, []);
    const baseFilteredUsers = React.useMemo(() => users.filter(user =>
        String(user.name || '').toLowerCase().includes(search.toLowerCase()) || String(user.phone || '').includes(search))
        .filter(user => selectedCountry === 'all' || user.country === selectedCountry)
        .filter(user => selectedCity === 'all' || user.city === selectedCity), [users, search, selectedCountry, selectedCity]);
    const filteredUsers = baseFilteredUsers.filter(user => filter === 'ALL' || user.status === filter);

    const formatDate = (dateStr) => {
        if (!dateStr) return null;
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return null;
        return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    return <main className="sk-page sk-page--wide sk-users">
        <PageHeader eyebrow="People" title="Users" description="Search accounts, review access, and inspect customer activity." />
        <Surface className="sk-users-toolbar" title="Find an account" description="Filter by status and location, or search a name or phone number.">
            <Toolbar right={<>
                <Select label="Country" value={selectedCountry} onChange={e => setSelectedCountry(e.target.value)} options={countries} />
                <Select label="City" value={selectedCity} onChange={e => setSelectedCity(e.target.value)} options={cities} />
                <Search value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name or phone…" />
            </>}>
                {['ALL', 'active', 'suspended', 'banned', 'terminated', 'deleted'].map(status => <Button key={status} aria-pressed={filter === status} variant={filter === status ? 'contained' : 'outlined'} color="primary" onClick={() => setFilter(status)}>{status === 'ALL' ? 'All Users' : status[0].toUpperCase() + status.slice(1)} ({status === 'ALL' ? baseFilteredUsers.length : baseFilteredUsers.filter(user => user.status === status).length})</Button>)}
            </Toolbar>
        </Surface>
        <Surface className="sk-users-table-surface">
            {loading && <div className="sk-state is-loading" role="status">Loading users…</div>}
            {!loading && filteredUsers.length === 0 && <Empty>No users match these filters.</Empty>}
            {!loading && filteredUsers.length > 0 && <div className="sk-table-wrap">
                <table className="sk-table sk-users-table">
                    <thead>
                        <tr>
                            <th>User</th>
                            <th>Location</th>
                            <th>Phone</th>
                            <th>Registered</th>
                            <th>Last sign-in</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredUsers.map(user => <tr key={user._id} className="sk-table-row-clickable" onClick={() => { window.location.href = `/#/users/${user._id}/show`; }} tabIndex={0} onKeyDown={e => { if (e.key === 'Enter') window.location.href = `/#/users/${user._id}/show`; }}>
                            <td>
                                <div className="sk-user-cell">
                                    <Avatar src={user.profilePic} alt={user.name} />
                                    <div className="sk-user-cell-text">
                                        <a className="sk-title" href={`/#/users/${user._id}/show`}>{user.name}</a>
                                        {(user.subscriptionTier && user.subscriptionTier !== 'FREE') && <StatusPill status="info">{user.subscriptionTier}</StatusPill>}
                                        {user.role === 'admin' && <StatusPill status="admin">Admin</StatusPill>}
                                    </div>
                                </div>
                            </td>
                            <td className="sk-cell-secondary">{user.city || 'Unknown city'}, {user.country || 'Unknown country'}</td>
                            <td className="sk-cell-secondary"><span className="sk-phone">{user.phone || 'No phone'}</span></td>
                            <td className="sk-cell-secondary">{formatDate(user.createdAt) || '—'}</td>
                            <td className="sk-cell-secondary">{user.lastLoginAt ? formatDate(user.lastLoginAt) : <span className="sk-muted">Never signed in</span>}</td>
                            <td><StatusPill status={user.status || 'active'} /></td>
                        </tr>)}
                    </tbody>
                </table>
            </div>}
        </Surface>
    </main>;
};

const ResourceFormHeader = ({ eyebrow, title, description }) => <PageHeader eyebrow={eyebrow} title={title} description={description} />;

export const UserEdit = props => <Edit {...props} className="sk-ra-page sk-resource-form sk-user-edit">
    <SimpleForm><ResourceFormHeader eyebrow="People · Edit" title="Edit user" description="Update account identity, permissions, and access status." />
        <div className="sk-form-section"><h2>Account settings</h2><div className="sk-form-grid"><TextInput source="name" fullWidth /><SelectInput source="role" fullWidth choices={[{ id: 'user', name: 'Regular User' }, { id: 'admin', name: 'Admin' }]} /><SelectInput source="status" fullWidth choices={[{ id: 'active', name: 'Active' }, { id: 'suspended', name: 'Suspended' }, { id: 'banned', name: 'Banned' }, { id: 'terminated', name: 'Terminated' }, { id: 'deleted', name: 'Deleted' }]} /></div></div>
        {/*
          * Subscription grants live in their own audited panel, NOT as a field
          * on this form. The SelectInput on `subscriptionTier` that used to sit
          * in Account settings above wrote the PAID claim: measured against the
          * live entitlement resolver, subscriptionTier=PRO yields source "paid"
          * / isPaid TRUE with no expiry, fabricating a paying customer with no
          * Stripe subscription behind it. The panel posts to the audited grant
          * endpoints, which write `grantedTier` (source "granted", isPaid
          * false) and require a reason.
          */}
        <FormDataConsumer>
            {({ formData }) => formData && formData.id ? (
                <SubscriptionGrantPanel
                    userId={formData.id}
                    currentGrantedTier={formData.grantedTier}
                />
            ) : null}
        </FormDataConsumer>
        <FormDataConsumer>
            {({ formData }) => formData && formData.role === 'admin' ? (
                <div className="sk-form-section" style={{ marginTop: 16 }}>
                    <h2>Admin Roles</h2>
                    <p style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>Assign one or more admin role bundles to control what this admin can access.</p>
                    <div style={{ borderRadius: 8, border: '1px solid #e0e0e0', padding: 12 }}>
                        <CheckboxGroupInput
                            source="adminRoles"
                            label=""
                            choices={ADMIN_ROLES.map(r => ({ id: r.id, name: r.name }))}
                            row={false}
                        />
                    </div>
                </div>
            ) : null}
        </FormDataConsumer>
    </SimpleForm>
</Edit>;

const LinkChips = ({ items = [], hrefFor, labelFor = item => item.name }) => items.length ? <div className="sk-chip-list">{items.map((item, index) => <a className="sk-link-chip" key={item._id || index} href={hrefFor(item)}>{labelFor(item)}</a>)}</div> : <span className="sk-muted">None</span>;

const TransactionReference = ({ tx }) => {
    if (!tx.id) return <span>—</span>;
    if (tx.method === 'stripe') return <a href={`https://dashboard.stripe.com/payments/${tx.id}`} target="_blank" rel="noopener noreferrer">{tx.id}</a>;
    if (tx.method === 'crypto') return <a href={`https://etherscan.io/tx/${tx.id}`} target="_blank" rel="noopener noreferrer">{tx.id}</a>;
    if (tx.method === 'bank') return <span>{tx.id}</span>;
    return <a href={`/#/requests/${tx.id}/show`}>{tx.id}</a>;
};

const TransactionsSurface = ({ transactions = [] }) => {
    const total = transactions.filter(tx => tx.status === 'confirmed' || tx.status === 'pending').reduce((sum, tx) => sum + (tx.type === 'credit' ? Number(tx.amount) : -Number(tx.amount)), 0).toFixed(2);
    return <Surface title="Transactions" description={`${transactions.length} account transactions`}><div className="sk-table-wrap"><table className="sk-data-table"><thead><tr><th>Reference</th><th>Type</th><th>Amount</th><th>Status</th><th>Method</th><th>External ID</th><th>Date</th></tr></thead><tbody>{transactions.map(tx => <tr key={tx._id}><td><code>{tx._id}</code></td><td>{String(tx.type || '').toUpperCase()}</td><td>{tx.amount} {tx.currency}</td><td><StatusPill status={tx.status} /></td><td>{tx.method || '—'}</td><td><code><TransactionReference tx={tx} /></code></td><td>{tx.createdAt ? new Date(tx.createdAt).toLocaleString() : '—'}</td></tr>)}</tbody>{transactions.length > 0 && <tfoot><tr><th>Total</th><td colSpan="6"><strong>{total} USD</strong></td></tr></tfoot>}</table>{!transactions.length && <Empty>No transactions</Empty>}</div></Surface>;
};

const RequestsGrouped = ({ label, items = [] }) => {
    const safe = Array.isArray(items) ? items : [];
    if (!safe.length) return null;
    const grouped = safe.reduce((acc, r) => { (acc[r.status] = acc[r.status] || []).push(r); return acc; }, {});
    return <Surface title={label} description={`${safe.length} requests`}><div className="sk-requests-grouped">{Object.entries(grouped).map(([status, reqs]) => <div key={status} className="sk-requests-group"><strong>{status} ({reqs.length})</strong><div className="sk-chip-list">{reqs.map((r, i) => <a className="sk-link-chip" key={r._id || i} href={`/#/requests/${r._id}/show`}>{r._id}</a>)}</div></div>)}</div></Surface>;
};

const VALID_TRANSITIONS = {
    active: ['suspended', 'banned', 'terminated'],
    suspended: ['active', 'banned', 'terminated'],
    banned: ['suspended', 'terminated'],
    terminated: ['deleted'],
};

const PolicyComplianceSurface = ({ record }) => {
    const consent = record.policyConsent || {};
    const flags = record.accountFlags || [];
    const sanctions = record.sanctionHistory || [];
    return <Surface title="Policy & Compliance" description="Account enforcement status and history">
        <DetailGrid>
            <DetailItem label="Cancellation Points" value={record.cancellationPoints ?? '0'} />
            <DetailItem label="Policy Accepted">{consent.accepted ? `✅ v${consent.version || '?'} — ${consent.acceptedAt ? new Date(consent.acceptedAt).toLocaleDateString() : '—'}` : '❌ Not accepted'}</DetailItem>
            <DetailItem wide label="Account Flags">{flags.length ? <div className="sk-chip-list">{flags.map((f, i) => <span className="sk-link-chip" key={i}>{f}</span>)}</div> : <span className="sk-muted">None</span>}</DetailItem>
            <DetailItem wide label="Sanction History">
                {sanctions.length ? <table className="sk-data-table" style={{ width: '100%', fontSize: 13 }}>
                    <thead><tr><th>Type</th><th>Reason</th><th>Issued</th><th>Expires</th><th>By</th></tr></thead>
                    <tbody>{sanctions.map((s, i) => <tr key={i}>
                        <td><StatusPill status={s.type || 'warning'}>{s.type || '—'}</StatusPill></td>
                        <td>{s.reason || '—'}</td>
                        <td>{s.issuedAt ? new Date(s.issuedAt).toLocaleDateString() : '—'}</td>
                        <td>{s.expiresAt ? new Date(s.expiresAt).toLocaleDateString() : 'Permanent'}</td>
                        <td>{s.issuedBy || '—'}</td>
                    </tr>)}</tbody>
                </table> : <span className="sk-muted">No sanctions</span>}
            </DetailItem>
        </DetailGrid>
    </Surface>;
};

const StateTransitionSurface = ({ record }) => {
    const currentStatus = record.status || 'active';
    const targets = VALID_TRANSITIONS[currentStatus] || [];
    const [targetStatus, setTargetStatus] = React.useState('');
    const [reason, setReason] = React.useState('');
    const [loading, setLoading] = React.useState(false);
    const [result, setResult] = React.useState(null);

    if (!targets.length) return null;

    const submit = () => {
        if (!targetStatus || !reason.trim()) return;
        setLoading(true);
        httpClient(`https://skilzapp.net/api/users/${record._id}`, {
            method: 'PATCH',
            body: JSON.stringify({ status: targetStatus, statusReason: reason })
        }).then(() => {
            setResult('Status updated — reload to see changes.');
            setTargetStatus('');
            setReason('');
        }).catch(() => setResult('Failed to update status.')).finally(() => setLoading(false));
    };

    return <Surface title="State Transition" description={`Current status: ${currentStatus}`}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div>
                <label style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Transition to</label>
                <select value={targetStatus} onChange={e => setTargetStatus(e.target.value)} style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #ccc' }}>
                    <option value="">Select status…</option>
                    {targets.map(t => <option key={t} value={t}>{t[0].toUpperCase() + t.slice(1)}</option>)}
                </select>
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
                <label style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Reason (required)</label>
                <input value={reason} onChange={e => setReason(e.target.value)} placeholder="Why is this change needed?" style={{ width: '100%', padding: '6px 10px', borderRadius: 8, border: '1px solid #ccc' }} />
            </div>
            <ActionButton disabled={loading || !targetStatus || !reason.trim()} onClick={submit}>{loading ? 'Updating…' : 'Apply'}</ActionButton>
        </div>
        {result && <p style={{ marginTop: 8, fontSize: 13, color: result.includes('Failed') ? '#d32f2f' : '#388e3c' }}>{result}</p>}
    </Surface>;
};

const SubscriptionSurface = ({ record }) => {
    const tier = record.subscriptionTier || 'FREE';
    const tierColors = { FREE: '#6b7280', PLUS: '#1C42FF', PRO: '#7c3aed', BUSINESS: '#f59e0b' };
    return <Surface title="Subscription" description={`Current plan: ${tier}`}>
        <DetailGrid>
            <DetailItem label="Plan"><StatusPill style={{ backgroundColor: tierColors[tier] || '#6b7280', color: '#fff' }}>{tier}</StatusPill></DetailItem>
            <DetailItem label="Started">{record.subscriptionStartedAt ? new Date(record.subscriptionStartedAt).toLocaleDateString() : '—'}</DetailItem>
            <DetailItem label="Expires">{record.subscriptionExpiresAt ? new Date(record.subscriptionExpiresAt).toLocaleDateString() : '—'}</DetailItem>
            <DetailItem label="Stripe Customer">{record.stripeCustomerId ? <code>{record.stripeCustomerId}</code> : '—'}</DetailItem>
            <DetailItem label="Stripe Subscription">{record.stripeSubscriptionId ? <a href={`https://dashboard.stripe.com/subscriptions/${record.stripeSubscriptionId}`} target="_blank" rel="noopener noreferrer"><code>{record.stripeSubscriptionId}</code></a> : '—'}</DetailItem>
        </DetailGrid>
    </Surface>;
};

const FraudFlagsSurface = ({ record }) => {
    const flags = record.fraudFlags || { count: 0, events: [] };
    const events = flags.events || [];
    const sevColors = { LOW: '#2196f3', MEDIUM: '#ff9800', HIGH: '#f44336', CRITICAL: '#b71c1c' };
    return <Surface title="Fraud Flags" description={`${flags.count || 0} active flags`}>
        {events.length ? <table className="sk-table" style={{ width: '100%', fontSize: 13 }}>
            <thead><tr><th>Reason</th><th>Severity</th><th>Description</th><th>Flagged</th><th>By</th><th>Status</th></tr></thead>
            <tbody>{events.map((e, i) => <tr key={i}>
                <td><code>{e.reason}</code></td>
                <td><StatusPill style={{ backgroundColor: sevColors[e.severity] || '#666', color: '#fff' }}>{e.severity}</StatusPill></td>
                <td>{e.description || '—'}</td>
                <td>{e.flaggedAt ? new Date(e.flaggedAt).toLocaleDateString() : '—'}</td>
                <td>{e.flaggedBy || '—'}</td>
                <td>{e.resolved ? <span style={{ color: '#388e3c' }}>✅ Resolved {e.resolvedAt ? new Date(e.resolvedAt).toLocaleDateString() : ''}</span> : <span style={{ color: '#d32f2f' }}>⚠️ Active</span>}</td>
            </tr>)}</tbody>
        </table> : <Empty>No fraud flags</Empty>}
    </Surface>;
};

const VerificationSurface = ({ record }) => {
    const level = record.verificationLevel || 0;
    const badges = { 0: '⚪ Not Verified', 1: '✅ Level 1 — ID Verified', 2: '✅✅ Level 2 — Fully Verified' };
    return <Surface title="Identity Verification" description={badges[level]}>
        <DetailGrid>
            <DetailItem label="Verification Level" value={`Level ${level}`} />
            <DetailItem label="Identity Verified">{record.identityVerified ? `✅ ${record.identityVerifiedAt ? new Date(record.identityVerifiedAt).toLocaleDateString() : ''}` : '❌ No'}</DetailItem>
            <DetailItem label="Address Verified">{record.addressVerified ? `✅ ${record.l2VerifiedAt ? new Date(record.l2VerifiedAt).toLocaleDateString() : ''}` : '❌ No'}</DetailItem>
            <DetailItem label="Attempt Count" value={record.verificationAttemptCount || 0} />
            <DetailItem label="Grace Deadline">{record.verificationGraceDeadline ? new Date(record.verificationGraceDeadline).toLocaleString() : '—'}</DetailItem>
            <DetailItem label="Trigger" value={record.verificationTrigger || '—'} />
        </DetailGrid>
    </Surface>;
};

const formatSource = source => source ? String(source)[0].toUpperCase() + String(source).slice(1) : 'KYC';

export const LegalNameSurface = ({ record }) => {
    const { permissions } = usePermissions();
    const adminRoles = (permissions && permissions.adminRoles) || [];
    const notify = useNotify();
    const refresh = useRefresh();
    const [loading, setLoading] = React.useState(false);
    const legalName = record.legalName || {};
    const lockedAt = record.legalNameLockedAt;
    const canUnlock = Boolean(lockedAt) && hasAccess('unlock-legal-name', adminRoles);

    const unlock = () => {
        if (!window.confirm(`Unlock the legal name for ${record.name || 'this user'}? They will be able to change it until the next KYC verification locks it again.`)) return;
        setLoading(true);
        httpClient(`${API_BASE_URL}/admin/users/${record._id || record.id}/unlock-name`, { method: 'POST' })
            .then(() => { notify('Legal name unlocked', 'info'); refresh(); })
            .catch(err => notify((err && err.message) || 'Failed to unlock legal name', 'warning'))
            .finally(() => setLoading(false));
    };

    return <Surface title="Legal name (KYC)" description="Name extracted from identity verification" actions={canUnlock && <ActionButton variant="danger" disabled={loading} onClick={unlock}>{loading ? 'Unlocking…' : 'Unlock name'}</ActionButton>}>
        <DetailGrid>
            <DetailItem label="Legal name (English)" value={legalName.en} />
            <DetailItem label="Legal name (Arabic)">{legalName.ar ? <span dir="rtl" lang="ar">{legalName.ar}</span> : '—'}</DetailItem>
            <DetailItem label="Lock state"><StatusPill>{lockedAt ? `Locked (${formatSource(record.legalNameSource)})` : 'Not locked'}</StatusPill></DetailItem>
            <DetailItem label="Locked at" value={lockedAt ? new Date(lockedAt).toLocaleString() : '—'} />
        </DetailGrid>
    </Surface>;
};

export const referralRewardsHref = userId => `/#/referral-rewards/${encodeURIComponent(userId)}`;

// One click from a user's profile to that user's referral rewards (as inviter).
// Shown only to roles that can open the Referral Rewards page.
export const ReferralRewardsAction = ({ userId }) => {
    const { permissions } = usePermissions();
    const adminRoles = (permissions && permissions.adminRoles) || [];
    if (!userId || !hasAccess('referral-rewards', adminRoles)) return null;
    return <ActionButton variant="secondary" type="button" onClick={() => window.location.assign(referralRewardsHref(userId))}>Referral rewards</ActionButton>;
};

export const NetworkSurface = ({ record }) => <Surface title="Network" actions={<ReferralRewardsAction userId={record._id || record.id} />}><DetailGrid><DetailItem wide label="Following"><LinkChips items={record.following} hrefFor={u => `/#/users/${u._id}/show`} /></DetailItem><DetailItem wide label="Followers"><LinkChips items={record.followers} hrefFor={u => `/#/users/${u._id}/show`} /></DetailItem><DetailItem wide label="Referrals"><LinkChips items={record.referrals} hrefFor={u => `/#/users/${u._id}/show`} labelFor={u => `${u.name} · ${u.phone}`} /></DetailItem></DetailGrid></Surface>;

const UserProfile = record => {
    const transactions = record.transactions || [];
    return <div className="sk-detail-page">
        <ProfileBand name={record.name || 'User profile'} subtitle={`${record.city || 'Unknown city'}, ${record.country || 'Unknown country'}`} avatar={<Avatar src={record.profilePic} alt={record.name} size="lg" />} status={<div className="sk-status-stack">{record.role === 'admin' && <StatusPill status="admin" />}<StatusPill status={record.status || 'active'} /></div>} />
        <Surface title="Profile details" description="Identity and account information"><DetailGrid><DetailItem label="Phone"><span className="sk-phone">{record.phone || '—'}</span></DetailItem><DetailItem label="Email" value={record.email} /><DetailItem label="Role" value={record.role} /><DetailItem label="Wallet" value={record.evmWallet} /><DetailItem label="User ID" value={record._id || record.id} /><DetailItem label="Created" value={record.createdAt ? new Date(record.createdAt).toLocaleString() : '—'} /></DetailGrid></Surface>
        <div className="sk-detail-columns">
            <NetworkSurface record={record} />
            <Surface title="Platform activity"><DetailGrid><DetailItem wide label="Services"><LinkChips items={record.UserServices} hrefFor={s => `/#/userservices/${s._id}/show`} labelFor={s => s.defaultService?.name || 'Service'} /></DetailItem><DetailItem wide label="Tags"><LinkChips items={record.tags} hrefFor={t => `/#/defaultservices/${t.as?._id}/show`} labelFor={t => t.as?.name || 'Tag'} /></DetailItem><DetailItem label="Feeds"><a href={`/#/feeds?createdBy=${record._id}`}>View feeds</a></DetailItem><DetailItem label="Tickets"><a href={`/#/tickets?createdBy=${record._id}`}>View tickets</a></DetailItem></DetailGrid></Surface>
        </div>
        <RequestsGrouped label="Sent requests" items={record.sentRequests} />
        <RequestsGrouped label="Received requests" items={record.receivedRequests} />
        <Surface title="Reports and cancellations"><DetailGrid><DetailItem wide label="Reports"><LinkChips items={record.recievedBlocksandReports} hrefFor={() => '#'} labelFor={r => r.reason || 'Report'} /></DetailItem><DetailItem wide label="Cancellation log"><Cancellations logs={record.cancelledRequests} /></DetailItem></DetailGrid></Surface>
        <TransactionsSurface transactions={transactions} />
        <SubscriptionSurface record={record} />
        <FraudFlagsSurface record={record} />
        <PolicyComplianceSurface record={record} />
        <VerificationSurface record={record} />
        <LegalNameSurface record={record} />
        <StateTransitionSurface record={record} />
        <Surface title="Start a support ticket" description="Open a new conversation for this user."><NewTicket userId={record._id} /></Surface>
    </div>;
};

export const UserShow = props => <Show {...props} className="sk-ra-page sk-resource-show sk-user-show"><SimpleShowLayout><FunctionField addLabel={false} render={UserProfile} /></SimpleShowLayout></Show>;

export const Cancellations = ({ logs = [] }) => {
    const safeLogs = Array.isArray(logs) ? logs : [];
    const [range, setRange] = React.useState('day');
    const now = new Date();
    const lastDay = new Date(now.getTime() - 86400000);
    const monday = new Date(now); const weekday = monday.getDay(); monday.setDate(monday.getDate() + (weekday === 0 ? -6 : 1 - weekday)); monday.setHours(0, 0, 0, 0);
    const month = new Date(now.getFullYear(), now.getMonth(), 1);
    const groups = { day: safeLogs.filter(log => new Date(log.createdAt) >= lastDay), week: safeLogs.filter(log => new Date(log.createdAt) >= monday), month: safeLogs.filter(log => new Date(log.createdAt) >= month), all: safeLogs };
    const labels = { day: 'Last 24hr', week: 'This Week', month: 'This Month', all: 'All' };
    const selected = [...groups[range]].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    return <div className="sk-cancellations"><div className="sk-tab-row" role="tablist" aria-label="Cancellation date range">{Object.keys(groups).map(key => <Button key={key} role="tab" aria-selected={range === key} variant={range === key ? 'contained' : 'outlined'} color="primary" onClick={() => setRange(key)}>{labels[key]} ({groups[key].length})</Button>)}</div><div className="sk-log-list">{selected.length ? selected.map((log, i) => <a key={i} href={`/#/requests/${log.relatedId}/show`} className={/SCHEDULED|IN_PROGRESS/.test(log.description || '') ? 'is-alert' : ''}><span>{log.description}</span><time>{new Date(log.createdAt).toLocaleString()}</time></a>) : <span className="sk-muted">No cancellations in this range.</span>}</div></div>;
};

const NewTicket = ({ userId }) => {
    const [loading, setLoading] = React.useState(false);
    const [subject, setSubject] = React.useState('');
    const submit = () => { setLoading(true); httpClient('https://skilzapp.net/api/tickets/new', { method: 'POST', body: JSON.stringify({ subject, userId }) }).then(response => { window.location.href = `/#/tickets/${response.json._id}/show`; }).finally(() => setLoading(false)); };
    return <div className="sk-inline-composer"><label htmlFor="new-ticket-subject">Ticket subject</label><div><input id="new-ticket-subject" value={subject} onChange={e => setSubject(e.target.value)} placeholder="How can support help?" /><ActionButton disabled={loading || !subject.trim()} onClick={submit}>{loading ? 'Creating…' : 'Create ticket'}</ActionButton></div></div>;
};
